/* StooretPulse theme scripts. Progressive enhancement only: every control has a working
   HTML fallback (links, forms, details) when this file has not loaded. */
(() => {
  'use strict';

  const theme = window.theme || { routes: {}, strings: {} };
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const CART_SECTION = 'cart-drawer';

  /* ---------- Utilities ---------- */

  const formatMoney = (cents, format = theme.moneyFormat || '€{{amount_with_comma_separator}}') => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = Number(cents) || 0;
    const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      const fixed = (number / 100).toFixed(precision);
      const [whole, fraction] = fixed.split('.');
      const withThousands = whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return fraction ? `${withThousands}${decimal}${fraction}` : withThousands;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount': return formatWithDelimiters(value, 2);
        case 'amount_no_decimals': return formatWithDelimiters(value, 0);
        case 'amount_with_comma_separator': return formatWithDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return formatWithDelimiters(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return formatWithDelimiters(value, 2, "'", '.');
        case 'amount_with_space_separator': return formatWithDelimiters(value, 2, ' ', ',');
        default: return formatWithDelimiters(value, 2);
      }
    });
  };
  theme.formatMoney = formatMoney;

  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };

  const announce = (message) => {
    const el = document.getElementById('GlobalStatus');
    if (!el) return;
    el.textContent = '';
    window.setTimeout(() => { el.textContent = message; }, 50);
  };

  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* ---------- Drawers (menu, search, cart) ---------- */

  class DrawerPanel extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.drawer__panel');
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]')) { e.preventDefault(); this.close(); }
        if (e.target.closest('[data-drawer-close-link]')) this.close();
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Tab') this.trapFocus(e);
      });
    }

    get isOpen() { return this.classList.contains('is-open'); }

    open(trigger) {
      if (this.isOpen) return;
      this.trigger = trigger || document.activeElement;
      this.setAttribute('aria-hidden', 'false');
      this.classList.add('is-open');
      document.body.classList.add('has-drawer');
      document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((el) => el.setAttribute('aria-expanded', 'true'));
      window.requestAnimationFrame(() => {
        const target = this.querySelector('input[type="search"]') || this.querySelector('.drawer__panel');
        target?.focus({ preventScroll: true });
      });
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      if (!document.querySelector('drawer-panel.is-open')) document.body.classList.remove('has-drawer');
      document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((el) => el.setAttribute('aria-expanded', 'false'));
      if (this.trigger && document.body.contains(this.trigger)) this.trigger.focus({ preventScroll: true });
    }

    trapFocus(e) {
      const items = [...this.panel.querySelectorAll(focusableSelector)].filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }
  customElements.define('drawer-panel', DrawerPanel);

  // Escape closes the open drawer even when focus was lost (for example after a cart re-render).
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('drawer-panel.is-open').forEach((d) => d.close());
  });

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-drawer-open]');
    if (!trigger) return;
    const drawer = document.getElementById(trigger.dataset.drawerOpen);
    if (!drawer || typeof drawer.open !== 'function') return;
    e.preventDefault();
    document.querySelectorAll('drawer-panel.is-open').forEach((d) => { if (d !== drawer) d.close(); });
    drawer.open(trigger);
  });

  /* ---------- Sticky header ---------- */

  class StickyHeader extends HTMLElement {
    connectedCallback() {
      const setHeight = () => document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
      setHeight();
      new ResizeObserver(setHeight).observe(this);
      if (!this.classList.contains('is-sticky')) return;
      const onScroll = () => this.classList.toggle('is-scrolled', window.scrollY > 8);
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }
  }
  customElements.define('sticky-header', StickyHeader);

  // Close desktop dropdowns when clicking elsewhere or pressing Escape.
  document.addEventListener('click', (e) => {
    document.querySelectorAll('details[data-dropdown][open]').forEach((d) => { if (!d.contains(e.target)) d.removeAttribute('open'); });
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('details[data-dropdown][open]').forEach((d) => { d.removeAttribute('open'); d.querySelector('summary')?.focus(); });
  });

  /* ---------- Rotating announcement bar ---------- */

  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.index = 0;
      if (this.slides.length < 2) return;
      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1, true));
      this.paused = false;
      ['mouseenter', 'focusin'].forEach((ev) => this.addEventListener(ev, () => { this.paused = true; }));
      ['mouseleave', 'focusout'].forEach((ev) => this.addEventListener(ev, () => { this.paused = false; }));
      if (this.dataset.autoplay === 'true' && !reducedMotion.matches) {
        this.timer = setInterval(() => { if (!this.paused) this.go(this.index + 1); }, (Number(this.dataset.speed) || 5) * 1000);
      }
    }
    disconnectedCallback() { clearInterval(this.timer); }
    go(i) {
      this.slides[this.index].hidden = true;
      this.slides[this.index].classList.remove('is-active');
      this.index = (i + this.slides.length) % this.slides.length;
      this.slides[this.index].hidden = false;
      this.slides[this.index].classList.add('is-active');
    }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  /* ---------- Hero slideshow ---------- */

  class HeroSlideshow extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.dots = [...this.querySelectorAll('[data-dot]')];
      this.index = 0;
      if (this.slides.length < 2) return;
      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1));
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.dot))));
      this.pauseButton = this.querySelector('[data-pause]');
      this.pauseButton?.addEventListener('click', () => this.togglePause());
      this.addEventListener('keydown', (e) => {
        if (e.target.closest('input, textarea')) return;
        if (e.key === 'ArrowLeft') this.go(this.index - 1);
        if (e.key === 'ArrowRight') this.go(this.index + 1);
      });
      // Touch swipe
      let startX = null;
      this.addEventListener('pointerdown', (e) => { if (e.pointerType !== 'mouse') startX = e.clientX; });
      this.addEventListener('pointerup', (e) => {
        if (startX === null) return;
        const delta = e.clientX - startX;
        if (Math.abs(delta) > 40) this.go(this.index + (delta < 0 ? 1 : -1));
        startX = null;
      });
      this.hovering = false;
      ['mouseenter', 'focusin'].forEach((ev) => this.addEventListener(ev, () => { this.hovering = true; }));
      ['mouseleave', 'focusout'].forEach((ev) => this.addEventListener(ev, () => { this.hovering = false; }));
      this.userPaused = false;
      if (this.dataset.autoplay === 'true' && !reducedMotion.matches) this.start();
      else this.setPausedUI(true);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    start() {
      clearInterval(this.timer);
      this.timer = setInterval(() => { if (!this.hovering && !this.userPaused && !document.hidden) this.go(this.index + 1, true); }, (Number(this.dataset.speed) || 7) * 1000);
    }
    togglePause() {
      this.userPaused = !this.userPaused;
      if (!this.userPaused && !this.timer) this.start();
      this.setPausedUI(this.userPaused || !this.timer);
    }
    setPausedUI(paused) {
      if (!this.pauseButton) return;
      this.pauseButton.setAttribute('aria-pressed', String(paused));
      this.pauseButton.setAttribute('aria-label', paused ? this.pauseButton.dataset.labelPlay : this.pauseButton.dataset.labelPause);
      this.pauseButton.classList.toggle('is-paused', paused);
    }
    go(i) {
      const next = (i + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, n) => {
        const active = n === next;
        slide.classList.toggle('is-active', active);
        slide.toggleAttribute('inert', !active);
        slide.setAttribute('aria-hidden', String(!active));
      });
      this.dots.forEach((dot, n) => (n === next ? dot.setAttribute('aria-current', 'true') : dot.removeAttribute('aria-current')));
      this.index = next;
    }
  }
  customElements.define('hero-slideshow', HeroSlideshow);

  /* ---------- Tabs (collections, routine) ---------- */

  class CollectionTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll(':scope > .tabs [role="tab"]')];
      if (!this.tabs.length) return;
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => {
          let next = null;
          if (e.key === 'ArrowRight') next = (i + 1) % this.tabs.length;
          if (e.key === 'ArrowLeft') next = (i - 1 + this.tabs.length) % this.tabs.length;
          if (e.key === 'Home') next = 0;
          if (e.key === 'End') next = this.tabs.length - 1;
          if (next !== null) { e.preventDefault(); this.select(next); this.tabs[next].focus(); }
        });
      });
    }
    select(index) {
      this.tabs.forEach((tab, i) => {
        const selected = i === index;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        const panel = document.getElementById(tab.getAttribute('aria-controls'));
        if (panel) panel.hidden = !selected;
      });
    }
  }
  customElements.define('collection-tabs', CollectionTabs);

  /* ---------- Predictive search (Shopify /search/suggest.json) ---------- */

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-results]');
      if (!this.input || !this.results || !theme.routes.predictiveSearch) return;
      this.input.addEventListener('input', debounce(() => this.search(), 250));
      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown') { const first = this.results.querySelector('a'); if (first) { e.preventDefault(); first.focus(); } }
      });
      this.results.addEventListener('keydown', (e) => {
        const links = [...this.results.querySelectorAll('a')];
        const i = links.indexOf(document.activeElement);
        if (e.key === 'ArrowDown' && i > -1) { e.preventDefault(); (links[i + 1] || links[0]).focus(); }
        if (e.key === 'ArrowUp' && i > -1) { e.preventDefault(); (i === 0 ? this.input : links[i - 1]).focus(); }
      });
    }
    async search() {
      const q = this.input.value.trim();
      this.controller?.abort();
      if (!q) { this.results.replaceChildren(); this.input.setAttribute('aria-expanded', 'false'); return; }
      this.controller = new AbortController();
      const url = new URL(`${theme.routes.predictiveSearch}.json`, window.location.origin);
      url.searchParams.set('q', q);
      url.searchParams.set('resources[type]', 'product,collection,page');
      url.searchParams.set('resources[limit]', '6');
      try {
        const res = await fetch(url, { signal: this.controller.signal });
        if (!res.ok) throw new Error(res.status);
        const data = await res.json();
        this.render(data.resources?.results || {}, q);
      } catch (err) {
        if (err.name !== 'AbortError') this.results.replaceChildren();
      }
    }
    render(results, q) {
      const list = document.createElement('ul');
      list.className = 'predictive-search__list';
      list.setAttribute('role', 'list');
      const add = (item, type) => {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.href = item.url;
        a.className = 'predictive-search__item';
        if (type === 'product' && item.featured_image?.url) {
          const img = document.createElement('img');
          const src = new URL(item.featured_image.url, window.location.origin);
          src.searchParams.set('width', '120');
          img.src = src.toString();
          img.alt = '';
          img.width = 56; img.height = 56; img.loading = 'lazy';
          a.append(img);
        }
        const text = document.createElement('span');
        text.className = 'predictive-search__text';
        const title = document.createElement('span');
        title.className = 'predictive-search__title';
        title.textContent = item.title;
        text.append(title);
        if (type === 'product' && item.price !== undefined) {
          const price = document.createElement('span');
          price.className = 'predictive-search__price';
          price.textContent = formatMoney(Math.round(parseFloat(item.price) * 100));
          text.append(price);
        }
        a.append(text);
        li.append(a);
        list.append(li);
      };
      (results.products || []).forEach((p) => add(p, 'product'));
      (results.collections || []).forEach((c) => add(c, 'collection'));
      (results.pages || []).forEach((p) => add(p, 'page'));
      const all = document.createElement('a');
      const searchUrl = new URL(this.querySelector('form').action, window.location.origin);
      searchUrl.searchParams.set('q', q);
      all.href = searchUrl.toString();
      all.className = 'link predictive-search__all';
      all.textContent = `${theme.strings.searchAll || 'Search for'} “${q}”`;
      if (!list.children.length) {
        const empty = document.createElement('p');
        empty.className = 'predictive-search__empty';
        empty.textContent = theme.strings.searchNoResults;
        this.results.replaceChildren(empty, all);
      } else {
        this.results.replaceChildren(list, all);
      }
      this.input.setAttribute('aria-expanded', 'true');
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ---------- Quantity input ---------- */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.addEventListener('click', (e) => {
        const button = e.target.closest('[data-step]');
        if (!button || !this.input) return;
        e.preventDefault();
        const step = Number(this.input.step) || 1;
        const min = this.input.min !== '' ? Number(this.input.min) : 0;
        const max = this.input.max !== '' ? Number(this.input.max) : Infinity;
        const current = Number(this.input.value) || 0;
        const next = Math.min(max, Math.max(min, current + Number(button.dataset.step) * step));
        if (next === current) return;
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------- Cart (Shopify AJAX API + Section Rendering API) ---------- */

  const cart = {
    async refreshCount(count) {
      let itemCount = count;
      if (itemCount === undefined) {
        const res = await fetch(`${theme.routes.cart}.js`, { headers: { Accept: 'application/json' } });
        itemCount = (await res.json()).item_count;
      }
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = itemCount;
        el.classList.toggle('is-empty', itemCount === 0);
      });
      return itemCount;
    },

    renderDrawer(sections) {
      const html = sections?.[CART_SECTION];
      const drawer = document.getElementById('CartDrawer');
      if (!html || !drawer) return;
      const fresh = parseHTML(html).querySelector('#CartDrawer .drawer__panel');
      const panel = drawer.querySelector('.drawer__panel');
      if (fresh && panel) {
        const focused = document.activeElement;
        const hadFocus = panel.contains(focused);
        const focusKey = hadFocus ? focused.closest('[data-line]')?.dataset.line : null;
        const focusStep = hadFocus ? focused.dataset.step : null;
        panel.innerHTML = fresh.innerHTML;
        drawer.panel = panel;
        if (hadFocus) {
          const selector = focusStep ? `[data-step="${focusStep}"]` : '[data-cart-quantity]';
          const sameControl = focusKey && panel.querySelector(`.cart-item[data-line="${focusKey}"] ${selector}`);
          (sameControl || panel).focus({ preventScroll: true });
        }
      }
    },

    async add(form) {
      const formData = new FormData(form);
      const drawerEnabled = theme.cartType === 'drawer' && document.getElementById('CartDrawer');
      if (drawerEnabled) {
        formData.append('sections', CART_SECTION);
        formData.append('sections_url', window.location.pathname);
      }
      const res = await fetch(`${theme.routes.cartAdd}.js`, { method: 'POST', body: formData, headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.cartError);
      if (!drawerEnabled) { window.location.href = theme.routes.cart; return data; }
      this.renderDrawer(data.sections);
      const count = await this.refreshCount();
      announce(`${theme.strings.added}. ${count}`);
      return data;
    },

    async change(line, quantity, sectionId) {
      const sections = [CART_SECTION];
      if (sectionId && sectionId !== CART_SECTION) sections.push(sectionId);
      const res = await fetch(`${theme.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections, sections_url: window.location.pathname })
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.cartError);
      return data;
    }
  };
  theme.cart = cart;

  // Every Shopify product form (PDP, featured product, quick add) adds through AJAX when JS is on.
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.action || !form.action.includes(theme.routes.cartAdd)) return;
    e.preventDefault();
    const submitter = e.submitter && e.submitter.form === form ? e.submitter : form.querySelector('[type="submit"][name="add"]');
    const buttons = [submitter, ...document.querySelectorAll(`[form="${form.id}"][name="add"]`)].filter(Boolean);
    const errorEl = form.querySelector('[data-form-error]');
    if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }
    buttons.forEach((b) => { b.disabled = true; b.setAttribute('aria-busy', 'true'); });
    try {
      await cart.add(form);
      document.getElementById('CartDrawer')?.open(submitter);
    } catch (err) {
      if (errorEl) { errorEl.textContent = err.message; errorEl.hidden = false; }
      else announce(err.message);
    } finally {
      buttons.forEach((b) => { b.removeAttribute('aria-busy'); if (!b.hasAttribute('data-unavailable')) b.disabled = false; });
    }
  });

  class CartItems extends HTMLElement {
    connectedCallback() {
      this.update = debounce((line, quantity) => this.change(line, quantity), 350);
      this.addEventListener('change', (e) => {
        const input = e.target.closest('[data-cart-quantity]');
        if (input) this.update(Number(input.dataset.line), Number(input.value));
        const note = e.target.closest('[data-cart-note]');
        if (note) fetch(`${theme.routes.cart}/update.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: note.value }) });
      });
      this.addEventListener('click', (e) => {
        const remove = e.target.closest('[data-cart-remove]');
        if (remove) { e.preventDefault(); this.change(Number(remove.dataset.line), 0); }
      });
    }
    async change(line, quantity) {
      const sectionId = this.dataset.section;
      const root = this.closest('drawer-panel') || this;
      root.classList.add('is-loading');
      try {
        const data = await cart.change(line, quantity, sectionId);
        cart.renderDrawer(data.sections);
        if (sectionId && sectionId !== CART_SECTION && data.sections?.[sectionId]) {
          const fresh = parseHTML(data.sections[sectionId]).querySelector('cart-items');
          const current = document.querySelector(`cart-items[data-section="${sectionId}"]`);
          if (fresh && current) current.innerHTML = fresh.innerHTML;
        }
        await cart.refreshCount(data.item_count);
        announce(document.querySelector('[data-cart-count-label]')?.textContent || '');
      } catch (err) {
        const errorEl = document.querySelector(`cart-items[data-section="${sectionId}"] [data-cart-error]`) || this.querySelector('[data-cart-error]');
        if (errorEl) { errorEl.textContent = err.message; errorEl.hidden = false; }
      } finally {
        root.classList.remove('is-loading');
      }
    }
  }
  customElements.define('cart-items', CartItems);

  /* ---------- Product gallery ---------- */

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.slides = [...this.track.children];
      this.thumbs = [...this.querySelectorAll('[data-thumb]')];
      this.counter = this.querySelector('[data-current]');
      this.live = this.querySelector('[data-live]');
      this.index = 0;
      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1));
      this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.go(Number(thumb.dataset.thumb))));
      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + 1); }
      });
      this.track.addEventListener('scroll', debounce(() => this.syncFromScroll(), 60), { passive: true });

      this.dialog = this.querySelector('[data-zoom-dialog]');
      this.querySelector('[data-zoom-open]')?.addEventListener('click', () => this.openZoom());
      this.querySelector('[data-zoom-close]')?.addEventListener('click', () => this.dialog.close());
      this.dialog?.addEventListener('click', (e) => { if (e.target === this.dialog) this.dialog.close(); });
      this.dialog?.addEventListener('close', () => { document.body.classList.remove('has-drawer'); this.querySelector('[data-zoom-open]')?.focus(); });
      this.slides.forEach((slide) => {
        const img = slide.querySelector('img');
        if (img && this.dialog) { img.style.cursor = 'zoom-in'; img.addEventListener('click', () => this.openZoom()); }
      });

      const selected = JSON.parse(this.querySelector('[data-selected-media]')?.textContent || 'null');
      if (selected) this.goToMedia(selected, true);
    }

    go(i, instant = false) {
      if (!this.slides.length) return;
      const next = Math.max(0, Math.min(this.slides.length - 1, i < 0 ? this.slides.length - 1 : i >= this.slides.length ? 0 : i));
      const slide = this.slides[next];
      this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: instant || reducedMotion.matches ? 'auto' : 'smooth' });
      this.setActive(next);
    }

    goToMedia(mediaId, instant = false) {
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (i > -1) this.go(i, instant);
    }

    syncFromScroll() {
      const width = this.track.clientWidth || 1;
      const i = Math.round(this.track.scrollLeft / width);
      if (i !== this.index) this.setActive(i);
    }

    setActive(i) {
      this.index = i;
      if (this.counter) this.counter.textContent = i + 1;
      this.thumbs.forEach((t, n) => (n === i ? t.setAttribute('aria-current', 'true') : t.removeAttribute('aria-current')));
      const thumb = this.thumbs[i];
      if (thumb) {
        const strip = thumb.closest('[data-thumbs]');
        const target = thumb.offsetLeft - strip.clientWidth / 2 + thumb.clientWidth / 2;
        strip.scrollTo({ left: target, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      }
      if (this.live) this.live.textContent = (theme.strings.imageCount || '').replace('[current]', i + 1).replace('[total]', this.slides.length);
    }

    openZoom() {
      if (!this.dialog) return;
      this.dialog.showModal();
      document.body.classList.add('has-drawer');
      const mediaId = this.slides[this.index]?.dataset.mediaId;
      const target = this.dialog.querySelector(`[data-media-id="${mediaId}"]`);
      target?.scrollIntoView({ block: 'start' });
      this.dialog.querySelector('[data-zoom-close]')?.focus();
    }
  }
  customElements.define('product-gallery', ProductGallery);

  /* ---------- Product info: variant selection, price, URL, sticky bar ---------- */

  class ProductInfo extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-product-json]');
      if (!json) return;
      this.product = JSON.parse(json.textContent);
      this.form = document.getElementById(this.dataset.formId);
      this.picker = this.querySelector('[data-variant-picker]');
      this.picker?.addEventListener('change', () => this.onOptionChange());
      this.setupStickyBar();
    }

    selectedOptions() {
      return [...this.picker.querySelectorAll('fieldset')].map((fs) => fs.querySelector('input:checked')?.value);
    }

    onOptionChange() {
      const options = this.selectedOptions();
      options.forEach((value, i) => {
        const label = this.querySelector(`[data-option-selected="${i}"]`);
        if (label) label.textContent = value;
      });
      const variant = this.product.variants.find((v) => v.options.every((opt, i) => opt === options[i]));
      this.updateAvailabilityMarkers(options);
      this.render(variant);
    }

    updateAvailabilityMarkers(options) {
      // Mark option values that have no available variant given the other current selections.
      this.picker.querySelectorAll('fieldset').forEach((fs, index) => {
        fs.querySelectorAll('input').forEach((input) => {
          const candidate = [...options];
          candidate[index] = input.value;
          const match = this.product.variants.find((v) => v.options.every((opt, i) => opt === candidate[i]));
          const label = fs.querySelector(`label[for="${input.id}"]`);
          label?.classList.toggle('is-unavailable', !match || !match.available);
        });
      });
    }

    render(variant) {
      const idInput = this.form?.querySelector('[data-variant-id]');
      const addButton = this.form?.querySelector('[data-add-button]');
      const addLabel = this.form?.querySelector('[data-add-label]');
      const dynamic = this.form?.querySelector('[data-dynamic-checkout]');
      const sticky = this.querySelector('[data-sticky-buy]');
      const stickyButton = sticky?.querySelector('[data-sticky-add]');

      if (!variant) {
        if (idInput) idInput.disabled = true;
        if (addButton) { addButton.disabled = true; addButton.setAttribute('data-unavailable', ''); }
        if (addLabel) addLabel.textContent = theme.strings.unavailable;
        if (stickyButton) { stickyButton.disabled = true; stickyButton.textContent = theme.strings.unavailable; }
        if (dynamic) dynamic.hidden = true;
        return;
      }

      if (idInput) { idInput.value = variant.id; idInput.disabled = !variant.available; }
      if (addButton) {
        addButton.disabled = !variant.available;
        addButton.toggleAttribute('data-unavailable', !variant.available);
      }
      if (addLabel) {
        addLabel.textContent = variant.available ? theme.strings.addToCart : theme.strings.soldOut;
        if (variant.available) {
          const price = document.createElement('span');
          price.className = 'product-form__submit-price';
          price.textContent = ` — ${formatMoney(variant.price)}`;
          addLabel.append(price);
        }
      }
      if (dynamic) dynamic.hidden = !variant.available;
      if (stickyButton) {
        stickyButton.disabled = !variant.available;
        stickyButton.textContent = variant.available ? theme.strings.addToCart : theme.strings.soldOut;
        const meta = sticky.querySelector('[data-sticky-variant]');
        if (meta && this.product.variants.length > 1) meta.textContent = `${variant.title} · `;
        const stickyPrice = sticky.querySelector('[data-sticky-price]');
        if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
      }

      this.renderPrice(variant);

      const sku = this.querySelector('[data-sku]');
      if (sku) { sku.hidden = !variant.sku; sku.querySelector('[data-sku-value]').textContent = variant.sku || ''; }

      const inventory = this.querySelector('[data-inventory]');
      if (inventory) {
        if (!variant.available) inventory.textContent = theme.strings.outOfStock;
        else if (variant.inventory_management !== 'shopify') inventory.textContent = '';
      }

      if (variant.featured_media_id) this.querySelector('product-gallery')?.goToMedia(variant.featured_media_id);

      if (this.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    renderPrice(variant) {
      const block = this.querySelector('[data-price-block] [data-price]');
      if (!block) return;
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      block.classList.toggle('price--on-sale', !!onSale);
      block.classList.toggle('price--sold-out', !variant.available);
      block.querySelector('[data-price-current]').textContent = formatMoney(variant.price);
      const compareWrap = block.querySelector('[data-price-compare-wrap]');
      if (compareWrap) {
        compareWrap.hidden = !onSale;
        compareWrap.querySelector('[data-price-compare]').textContent = onSale ? formatMoney(variant.compare_at_price) : '';
      }
      const badge = block.querySelector('[data-price-badge]');
      if (badge) {
        badge.hidden = !onSale;
        badge.textContent = onSale ? (theme.strings.savePercentTemplate || '').replace('[percent]', Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price)) : '';
      }
      const unit = block.querySelector('[data-unit-price]');
      if (unit) {
        unit.hidden = !variant.unit_price;
        unit.textContent = variant.unit_price ? `${formatMoney(variant.unit_price)}/${variant.unit_price_reference && variant.unit_price_reference !== 1 ? variant.unit_price_reference : ''}${variant.unit_price_unit || ''}` : '';
      }
    }

    setupStickyBar() {
      const bar = this.querySelector('[data-sticky-buy]');
      const anchor = this.form?.querySelector('[data-add-button]');
      if (!bar || !anchor) return;
      let ticking = false;
      const update = () => {
        ticking = false;
        const passed = anchor.getBoundingClientRect().bottom < 0;
        if (bar.hidden === !passed) return;
        bar.hidden = !passed;
        bar.classList.toggle('is-visible', passed);
        document.body.classList.toggle('has-sticky-buy', passed);
      };
      window.addEventListener('scroll', () => { if (!ticking) { ticking = true; window.requestAnimationFrame(update); } }, { passive: true });
      update();
    }
  }
  customElements.define('product-info', ProductInfo);

  /* ---------- Share ---------- */

  class ShareButton extends HTMLElement {
    connectedCallback() {
      const button = this.querySelector('button');
      const status = this.querySelector('[data-share-status]');
      button?.addEventListener('click', async () => {
        const url = new URL(window.location.href).toString();
        const title = button.dataset.shareTitle;
        try {
          if (navigator.share) { await navigator.share({ title, url }); return; }
          await navigator.clipboard.writeText(url);
          if (status) status.textContent = theme.strings.shareCopied;
          button.textContent = theme.strings.shareCopied;
        } catch (err) { /* share dismissed */ }
      });
    }
  }
  customElements.define('share-button', ShareButton);

  /* ---------- Product recommendations ---------- */

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.children.length || !this.dataset.url) return;
      const observer = new IntersectionObserver(async ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        try {
          const res = await fetch(this.dataset.url);
          const fresh = parseHTML(await res.text()).querySelector('product-recommendations');
          if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
        } catch (err) { /* keep section empty */ }
      }, { rootMargin: '0px 0px 400px 0px' });
      observer.observe(this);
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ---------- Small behaviors ---------- */

  document.addEventListener('change', (e) => {
    if (e.target.matches('[data-auto-submit]')) e.target.form?.submit();
  });

  if (document.body.classList.contains('has-animations') && !reducedMotion.matches && 'IntersectionObserver' in window) {
    const reveal = new IntersectionObserver((entries) => {
      entries.forEach((entry) => { if (entry.isIntersecting) { entry.target.classList.add('is-visible'); reveal.unobserve(entry.target); } });
    }, { threshold: 0.12 });
    document.querySelectorAll('.reveal').forEach((el) => { el.classList.add('reveal--pending'); reveal.observe(el); });
  }

  // Theme editor: keep drawers and slideshows in sync when sections reload.
  document.addEventListener('shopify:block:select', (e) => {
    const slideshow = e.target.closest('hero-slideshow');
    if (slideshow) {
      const i = slideshow.slides?.indexOf(e.target);
      if (i > -1) slideshow.go(i);
    }
  });
})();
